import {
  PriceListItem,
  PriceTimeVariant,
  PriceType,
  PurchaseAgreement,
} from '@retail/purchase-agreements/types';

import { useMemo, useState } from 'react';
import {
  useExportPriceLists,
  useFetchAgreementPricesComparison,
} from '@retail/purchase-agreements/data-access';
import { usePagination } from '@shared/hooks';
import { PriceListTable } from '@retail/purchase-agreements/components';

interface Props {
  assortmentId: number;
  selectedAgreementIds: number[];
  selectedAgreements: PurchaseAgreement[];
  selectedProductGroup?: string;
  query?: string;
  checkedPriceType: PriceTimeVariant;
  includeSearchField?: boolean;
}

export function PriceListTableContainer({
  assortmentId,
  selectedAgreementIds,
  selectedAgreements,
  selectedProductGroup,
  query,
  checkedPriceType,
  includeSearchField = false,
}: Props) {
  const pagination = usePagination();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: agreementPriceComparisonPaginationDTO, isFetching } =
    useFetchAgreementPricesComparison(
      {
        assortmentId,
        agreementIds: selectedAgreementIds,
        groupNumbers: selectedProductGroup ? [selectedProductGroup] : [],
        current: checkedPriceType === 'CURRENT',
        active: !(checkedPriceType === 'EXPIRED'),
        query: query ?? searchQuery,
        page: pagination.page,
        size: pagination.pageSize,
      },
      { enabled: selectedAgreementIds.length > 0, suspense: false }
    );
  const priceListItems = useMemo(
    () => (agreementPriceComparisonPaginationDTO?.result ?? []).map(PriceListItem.fromDto),
    [agreementPriceComparisonPaginationDTO?.result]
  );

  const priceListItemPrices: PriceListItem[] = useMemo(
    () =>
      priceListItems
        .map((item) => {
          const netPrices = item.agreementPrices.filter(
            (price) => price.priceType === PriceType.NET
          );
          const gtinPrices = item.agreementPrices.filter(
            (price) => price.priceType === PriceType.GTIN
          );

          return !netPrices.length || !gtinPrices.length
            ? item
            : [
                { ...item, agreementPrices: netPrices },
                { ...item, agreementPrices: gtinPrices },
              ];
        })
        .flat(),
    [priceListItems]
  );

  const totalItems = useMemo(
    () => agreementPriceComparisonPaginationDTO?.totalCount,
    [agreementPriceComparisonPaginationDTO?.totalCount]
  );

  if (!!totalItems && totalItems !== pagination.totalCount) {
    pagination.setTotalCount(totalItems);
  }

  const { exportToExcel, isLoading } = useExportPriceLists(
    {
      assortmentId,
      agreementIds: selectedAgreementIds,
      groupNumbers: selectedProductGroup ? [selectedProductGroup] : [],
      current: checkedPriceType === 'CURRENT',
      active: !(checkedPriceType === 'EXPIRED'),
      query: query ?? searchQuery,
      page: pagination.page,
      size: priceListItems.length,
    },
    selectedAgreements
  );

  return (
    <PriceListTable
      selectedAgreements={selectedAgreements}
      priceListItemPrices={priceListItemPrices}
      priceListItemLength={priceListItems.length}
      pagination={pagination}
      isFetching={isFetching}
      isLoadingExport={isLoading}
      includeSearchField={includeSearchField}
      exportToExcel={exportToExcel}
      setSearchQuery={setSearchQuery}
    />
  );
}
